import type React from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, Mail, MapPin, Phone } from "lucide-react";
import { articleJsonLd, breadcrumbsJsonLd, productJsonLd, serviceJsonLd } from "@latam-energy/seo";
import { blogPosts, brands, downloads, industries, products, projects, services, siteConfig, solutions } from "@latam-energy/content";
import type { Industry, Project, Service, Solution } from "@latam-energy/types";
import { AppImage as Image } from "@/components/AppImage";
import { AppLink as Link } from "@/components/AppLink";
import { BlogCard, IndustryCard, ProductCard, ProjectCard, ServiceCard, SolutionCard } from "@/components/Cards";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ContactForm, NewsletterForm } from "@/components/Forms";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { PageHero } from "@/components/PageHero";
import { ProductCatalog } from "@/components/ProductCatalog";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SectionHeading } from "@/components/SectionHeading";
import { StructuredData } from "@/components/StructuredData";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AnalyticsScripts } from "@/lib/analytics";
import { formatDate, siteUrl, slugLabel, whatsappHref } from "@/lib/utils";
import { usePageSeo } from "@/seo";

export function App() {
  return (
    <>
      <ScrollToTop />
      <Header />
      <main id="contenido">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/nosotros" element={<AboutPage />} />
          <Route path="/servicios" element={<ServicesPage />} />
          <Route path="/servicios/:slug" element={<ServiceDetailPage />} />
          <Route path="/soluciones" element={<SolutionsPage />} />
          <Route path="/soluciones/:slug" element={<SolutionDetailPage />} />
          <Route path="/productos" element={<ProductsPage />} />
          <Route path="/productos/:slug" element={<ProductDetailPage />} />
          <Route path="/categorias/:slug" element={<CategoryPage />} />
          <Route path="/marcas" element={<BrandsPage />} />
          <Route path="/industrias" element={<IndustriesPage />} />
          <Route path="/industrias/:slug" element={<IndustryDetailPage />} />
          <Route path="/proyectos" element={<ProjectsPage />} />
          <Route path="/proyectos/:slug" element={<ProjectDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/descargas" element={<DownloadsPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/cotizar" element={<QuotePage />} />
          <Route path="/politica-de-privacidad" element={<PrivacyPage />} />
          <Route path="/terminos-y-condiciones" element={<TermsPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <AnalyticsScripts />
    </>
  );
}

function HomePage() {
  usePageSeo({
    title: siteConfig.defaultSeoTitle,
    description: siteConfig.defaultSeoDescription,
    pathname: "/"
  });

  const proofPoints = [
    ["6", "líneas técnicas"],
    ["16", "equipos referenciales"],
    ["PE", "base operativa"],
    ["B2B", "cotización consultiva"]
  ];

  return (
    <>
      <section className="hero-surface text-white">
        <div className="container hero-layout grid items-center gap-7 py-8 md:py-10 lg:grid-cols-[1fr_0.9fr]">
          <div className="max-w-2xl">
            <span className="badge badge--primary">INSTRUMENTACIÓN, REPUESTOS, INGENIERÍA</span>
            <h1 className="mt-4 max-w-3xl text-[2rem] font-black leading-[1.08] md:text-4xl lg:text-5xl">Ingeniería, Instrumentación y Automatización para operaciones críticas</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-200 md:text-lg md:leading-8">Integramos soluciones técnicas para plantas que necesitan continuidad, medición confiable y soporte de ingeniería desde la selección hasta la puesta en marcha.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link className="btn btn-primary" href="/cotizar">Solicitar cotización</Link>
              <Link className="btn btn-secondary" href="/servicios">Explorar capacidades <ArrowRight size={18} /></Link>
            </div>
            <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
              {proofPoints.map(([value, label]) => (
                <div className="metric-tile" key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="industrial-panel">
            <div className="panel-toolbar"><span /> <span /> <span /></div>
            <Image src="/assets/industrial/automation-controls.jpg" alt="Tablero de automatización y control industrial" width={720} height={520} priority className="mx-auto aspect-[16/10] w-full max-w-[500px] rounded-md object-cover" />
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {["CONTROL", "MEDICIÓN", "INGENIERÍA"].map((item) => <span className="signal-pill" key={item}>{item}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section className="trust-strip">
        <div className="container grid gap-3 text-sm font-bold text-navy md:grid-cols-3">
          {["Equipos originales y marcas premium", "Respuesta rápida por WhatsApp", "Soporte técnico para selección"].map((item) => (
            <div className="trust-item flex items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-3" key={item}>
              <CheckCircle2 className="shrink-0 text-energy" size={18} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeading kicker="Servicios" title="Capacidades técnicas principales" text="Un portafolio pensado para automatización industrial, instrumentación, control, mantenimiento e integración de sistemas." />
          <div className="grid-auto">{services.map((service) => <ServiceCard key={service.slug} service={service} />)}</div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          <SectionHeading kicker="Industrias" title="Aplicaciones por sector" text="Soluciones para petróleo, gas, minería, manufactura, energía, agua, utilities e infraestructura técnica." />
          <div className="grid-auto">{industries.map((industry) => <IndustryCard key={industry.slug} industry={industry} />)}</div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeading kicker="Catálogo" title="Equipos destacados para cotización" text="Catálogo técnico sin precios ni carrito: cada producto se evalúa según aplicación, rango y condiciones de operación." />
          <div className="grid-auto">{products.filter((product) => product.featured).slice(0, 6).map((product) => <ProductCard key={product.slug} product={product} />)}</div>
          <Link className="btn btn-outline mt-8" href="/productos">Ver catálogo completo</Link>
        </div>
      </section>

      <CtaBand />
      <StructuredData data={[{ "@context": "https://schema.org", "@type": "Organization", name: siteConfig.companyName, url: siteUrl(), email: siteConfig.email, telephone: siteConfig.telephone }, { "@context": "https://schema.org", "@type": "WebSite", name: siteConfig.companyName, url: siteUrl(), inLanguage: "es-PE" }]} />
    </>
  );
}

function ServicesPage() {
  usePageSeo({ title: "Servicios industriales | Latam Energy Services", description: "Servicios de automatización, instrumentación, electricidad, energía, integración y mantenimiento industrial.", pathname: "/servicios" });
  return <ListingPage kicker="Servicios" title="Servicios industriales" text="Capacidades técnicas para proyectos, mantenimiento, suministro especializado e integración industrial." items={services} render={(service: Service) => <ServiceCard key={service.slug} service={service} />} />;
}

function ServiceDetailPage() {
  const service = findBySlug(services, useParams().slug);
  if (!service) return <Navigate to="/404" replace />;
  const crumbs = crumbsFor("Servicios", "/servicios", service.title, `/servicios/${service.slug}`);
  usePageSeo({ title: service.seoTitle, description: service.seoDescription, pathname: `/servicios/${service.slug}` });
  return (
    <>
      <Breadcrumbs items={crumbs} />
      <PageHero kicker="Servicio" title={service.title} text={service.summary} />
      <section className="section">
        <div className="container grid gap-8 md:grid-cols-[1.2fr_0.8fr]">
          <article className="prose-industrial"><h2 className="text-3xl font-extrabold text-navy">Alcance técnico</h2><p>{service.description}</p><h2 className="mt-8 text-2xl font-extrabold text-navy">Aplicaciones</h2><ul>{service.applications.map((item) => <li key={item}>{item}</li>)}</ul></article>
          <aside className="card h-fit p-6"><h2 className="text-xl font-extrabold text-navy">Entregables</h2><ul className="mt-4 grid gap-3 text-slate-600">{service.deliverables.map((item) => <li key={item}>{item}</li>)}</ul></aside>
        </div>
      </section>
      <StructuredData data={[serviceJsonLd(service, siteUrl()), breadcrumbsJsonLd(crumbs, siteUrl())]} />
    </>
  );
}

function SolutionsPage() {
  usePageSeo({ title: "Soluciones industriales | Latam Energy Services", description: "Soluciones por aplicación para monitoreo, adquisición de datos, control de procesos, energía y motores.", pathname: "/soluciones" });
  return <ListingPage kicker="Soluciones" title="Soluciones por aplicación" text="Arquitecturas técnicas para resolver necesidades recurrentes en operaciones industriales." items={solutions} render={(solution: Solution) => <SolutionCard key={solution.slug} solution={solution} />} />;
}

function SolutionDetailPage() {
  const solution = findBySlug(solutions, useParams().slug);
  if (!solution) return <Navigate to="/404" replace />;
  usePageSeo({ title: solution.seoTitle, description: solution.seoDescription, pathname: `/soluciones/${solution.slug}` });
  return <DetailListPage kicker="Solucion" title={solution.title} text={solution.summary} heading="Componentes habituales" items={solution.components} />;
}

function ProductsPage() {
  usePageSeo({ title: "Catálogo de equipos industriales | Latam Energy Services", description: "Catálogo para cotización de instrumentación, sensores, válvulas, automatización, variadores y monitoreo energético.", pathname: "/productos" });
  return (
    <>
      <PageHero kicker="Catálogo" title="Productos y equipos para cotización" text="Busca por texto, categoría, marca referencial o aplicación. No se muestran precios porque cada selección requiere validación técnica." />
      <section className="section"><div className="container"><ProductCatalog products={products} /></div></section>
    </>
  );
}

function ProductDetailPage() {
  const product = findBySlug(products, useParams().slug);
  if (!product) return <Navigate to="/404" replace />;
  const related = products.filter((item) => item.category === product.category && item.slug !== product.slug).slice(0, 3);
  const crumbs = crumbsFor("Productos", "/productos", product.name, `/productos/${product.slug}`);
  usePageSeo({ title: product.seoTitle, description: product.seoDescription, pathname: `/productos/${product.slug}` });
  return (
    <>
      <Breadcrumbs items={crumbs} />
      <section className="section pt-8">
        <div className="container grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <Image src={product.mainImage} alt={`Imagen referencial de ${product.name}`} width={720} height={520} priority className="card aspect-[4/3] w-full object-cover" />
          <div>
            <p className="font-extrabold uppercase text-cyan">{product.category} · {product.brand}</p>
            <h1 className="mt-3 text-4xl font-black text-navy md:text-5xl">{product.name}</h1>
            <p className="mt-4 text-lg leading-8 text-slate-600">{product.description}</p>
            <div className="mt-6 flex flex-wrap gap-3"><Link className="btn btn-primary" href={`/cotizar?producto=${product.slug}`}>Solicitar cotización</Link><a className="btn btn-outline" href={whatsappHref(`Hola, deseo cotizar ${product.name}.`)}>WhatsApp</a></div>
          </div>
        </div>
      </section>
      <section className="section bg-white"><div className="container grid gap-8 md:grid-cols-3"><Spec title="Caracteristicas" items={product.features} /><Spec title="Aplicaciones" items={product.applications} /><Spec title="Descargas" items={product.downloads.map((download) => download.label)} /></div></section>
      <section className="section"><div className="container"><h2 className="mb-6 text-3xl font-extrabold text-navy">Productos relacionados</h2><div className="grid-auto">{related.map((item) => <ProductCard key={item.slug} product={item} />)}</div></div></section>
      <StructuredData data={[productJsonLd(product, siteUrl()), breadcrumbsJsonLd(crumbs, siteUrl())]} />
    </>
  );
}

function CategoryPage() {
  const slug = useParams().slug ?? "";
  const items = products.filter((product) => product.category.toLowerCase().replace(/\s+/g, "-") === slug);
  if (!items.length) return <Navigate to="/404" replace />;
  const title = `Categoría: ${slugLabel(slug)}`;
  usePageSeo({ title: `${title} | Latam Energy Services`, description: `Productos referenciales para ${slugLabel(slug)} y cotización técnica industrial.`, pathname: `/categorias/${slug}` });
  return <ListingPage kicker="Categoría" title={title} text="Equipos agrupados por familia técnica para acelerar la selección inicial." items={items} render={(product) => <ProductCard key={product.slug} product={product} />} />;
}

function IndustriesPage() {
  usePageSeo({ title: "Industrias atendidas | Latam Energy Services", description: "Soluciones para petróleo y gas, minería, manufactura, energía, agua, utilities e infraestructura.", pathname: "/industrias" });
  return <ListingPage kicker="Industrias" title="Industrias atendidas" text="Aplicaciones por sector para priorizar continuidad, medición confiable e integración de datos." items={industries} render={(industry: Industry) => <IndustryCard key={industry.slug} industry={industry} />} />;
}

function IndustryDetailPage() {
  const industry = findBySlug(industries, useParams().slug);
  if (!industry) return <Navigate to="/404" replace />;
  usePageSeo({ title: industry.seoTitle, description: industry.seoDescription, pathname: `/industrias/${industry.slug}` });
  return <DetailListPage kicker="Industria" title={industry.name} text={industry.summary} heading="Necesidades frecuentes" items={industry.needs} />;
}

function ProjectsPage() {
  usePageSeo({ title: "Proyectos y aplicaciones | Latam Energy Services", description: "Casos referenciales de automatización, instrumentación, energía y mantenimiento industrial.", pathname: "/proyectos" });
  return <ListingPage kicker="Proyectos" title="Proyectos y aplicaciones" text="Casos referenciales para mostrar alcance técnico y resultados esperados." items={projects} render={(project: Project) => <ProjectCard key={project.slug} project={project} />} />;
}

function ProjectDetailPage() {
  const project = findBySlug(projects, useParams().slug);
  if (!project) return <Navigate to="/404" replace />;
  usePageSeo({ title: project.seoTitle, description: project.seoDescription, pathname: `/proyectos/${project.slug}` });
  return <DetailListPage kicker={project.industry} title={project.title} text={`${project.summary} ${project.result}`} heading="Alcance referencial" items={project.scope} />;
}

function BlogPage() {
  usePageSeo({ title: "Blog técnico industrial | Latam Energy Services", description: "Artículos sobre automatización, instrumentación, eficiencia energética e integración de sistemas industriales.", pathname: "/blog" });
  return <ListingPage kicker="Blog técnico" title="Artículos recientes" text="Guías prácticas para decisiones de automatización, instrumentación y energía industrial." items={blogPosts} render={(post) => <BlogCard key={post.slug} post={post} />} />;
}

function BlogPostPage() {
  const post = findBySlug(blogPosts, useParams().slug);
  if (!post) return <Navigate to="/404" replace />;
  const crumbs = crumbsFor("Blog", "/blog", post.title, `/blog/${post.slug}`);
  usePageSeo({ title: post.seoTitle, description: post.seoDescription, pathname: `/blog/${post.slug}` });
  return <><Breadcrumbs items={crumbs} /><article className="container max-w-3xl py-12"><p className="font-extrabold uppercase text-cyan">{post.category} · {formatDate(post.date)} · {post.readingTime}</p><h1 className="mt-4 text-4xl font-black text-navy md:text-6xl">{post.title}</h1><p className="mt-5 text-xl leading-8 text-slate-600">{post.summary}</p><div className="prose-industrial mt-8 grid gap-5">{post.content.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div></article><StructuredData data={[articleJsonLd(post, siteUrl()), breadcrumbsJsonLd(crumbs, siteUrl())]} /></>;
}

function AboutPage() {
  usePageSeo({ title: "Nosotros | Latam Energy Services", description: "Aliado técnico para empresas industriales que requieren automatización, instrumentación, energía y soporte especializado.", pathname: "/nosotros" });
  return <DetailListPage kicker="Nosotros" title="Soporte técnico con enfoque de ingeniería aplicada" text="Latam Energy Services acompaña operaciones industriales con criterio técnico, documentación clara y una ruta de cotización consultiva." heading="Diferenciales" items={["Evaluación técnica antes de cotizar", "Conocimiento de instrumentación, control y energía", "Soporte para proyectos y mantenimiento", "Comunicación clara con equipos de operaciones"]} />;
}

function BrandsPage() {
  usePageSeo({ title: "Marcas referenciales | Latam Energy Services", description: "Marcas y fabricantes referenciales para proyectos industriales. No implica representación oficial sin confirmación.", pathname: "/marcas" });
  return <ListingPage kicker="Marcas" title="Marcas y fabricantes referenciales" text="La grilla usa placeholders y no implica representación oficial. Sustituir por marcas autorizadas verificadas." items={brands} render={(brand) => <div className="card grid min-h-24 place-items-center p-4 text-center font-extrabold text-steel" key={brand}>{brand}</div>} />;
}

function DownloadsPage() {
  usePageSeo({ title: "Descargas técnicas | Latam Energy Services", description: "Catálogos, fichas técnicas y recursos descargables referenciales para soluciones industriales.", pathname: "/descargas" });
  return <ListingPage kicker="Descargas" title="Recursos técnicos" text="Documentos referenciales para iniciar conversaciones técnicas y solicitudes de cotización." items={downloads} render={(download) => <a className="card flex items-center gap-3 p-6 hover:border-cyan" href={download.href} key={download.href}><Download className="text-cyan" /><span className="font-extrabold text-navy">{download.label}</span></a>} />;
}

function ContactPage() {
  usePageSeo({ title: "Contacto | Latam Energy Services", description: "Contacta a Latam Energy Services para automatización industrial, instrumentación, control, energía y mantenimiento.", pathname: "/contacto" });
  return (
    <>
      <PageHero kicker="Contacto" title="Conversemos sobre tu requerimiento técnico" text="Comparte tu aplicación, variable, equipo o proyecto. Te responderemos con una ruta clara de evaluación." />
      <section className="section"><div className="container grid gap-8 lg:grid-cols-[0.85fr_1.15fr]"><ContactPanel /><ContactForm /></div></section>
    </>
  );
}

function QuotePage() {
  usePageSeo({ title: "Solicitar cotización | Latam Energy Services", description: "Formulario de solicitud de cotización para productos, equipos, automatización, instrumentación y servicios industriales.", pathname: "/cotizar" });
  return <><PageHero kicker="Cotización" title="Solicita una cotización técnica" text="Incluye condiciones de operación, cantidad estimada, marca/modelo si aplica y el método de contacto preferido." /><section className="section"><div className="container max-w-5xl"><ContactForm kind="cotizacion" /></div></section></>;
}

function PrivacyPage() {
  usePageSeo({ title: "Política de privacidad | Latam Energy Services", description: "Política de privacidad referencial para formularios, contacto y analítica opcional del sitio.", pathname: "/politica-de-privacidad" });
  return <LegalPage title="Política de privacidad" items={["Usamos los datos enviados en formularios solo para responder solicitudes comerciales y técnicas.", "El sitio puede usar analítica si se configuran identificadores de medición.", "Puedes solicitar actualización o eliminación de tus datos escribiendo al correo de contacto."]} />;
}

function TermsPage() {
  usePageSeo({ title: "Términos y condiciones | Latam Energy Services", description: "Términos de uso referenciales del sitio web de Latam Energy Services.", pathname: "/terminos-y-condiciones" });
  return <LegalPage title="Términos y condiciones" items={["La información técnica y comercial del sitio es referencial y debe validarse antes de compra o instalación.", "Las marcas mostradas no implican representación oficial sin confirmación expresa.", "Toda cotización está sujeta a disponibilidad, especificación técnica y condiciones comerciales acordadas."]} />;
}

function NotFoundPage() {
  usePageSeo({ title: "Página no encontrada | Latam Energy Services", description: "La página solicitada no existe o fue movida.", pathname: "/404", noIndex: true });
  return <><PageHero kicker="404" title="Página no encontrada" text="La ruta solicitada no existe o fue movida." /><section className="section"><div className="container"><Link className="btn btn-primary" href="/">Volver al inicio</Link></div></section></>;
}

function ListingPage<T>({ kicker, title, text, items, render }: { kicker: string; title: string; text: string; items: readonly T[]; render: (item: T) => React.ReactNode }) {
  return <><PageHero kicker={kicker} title={title} text={text} /><section className="section"><div className="container"><div className="grid-auto">{items.map(render)}</div></div></section></>;
}

function DetailListPage({ kicker, title, text, heading, items }: { kicker: string; title: string; text: string; heading: string; items: string[] }) {
  return <><PageHero kicker={kicker} title={title} text={text} /><section className="section"><div className="container grid gap-8 md:grid-cols-[1.2fr_0.8fr]"><article className="prose-industrial"><h2 className="text-3xl font-extrabold text-navy">Resumen técnico</h2><p>{text}</p></article><aside className="card h-fit p-6"><h2 className="text-xl font-extrabold text-navy">{heading}</h2><ul className="mt-4 grid gap-3 text-slate-600">{items.map((item) => <li key={item} className="flex gap-2"><CheckCircle2 className="mt-1 shrink-0 text-energy" size={18} />{item}</li>)}</ul></aside></div></section></>;
}

function CtaBand() {
  return (
    <section className="section bg-steel text-white">
      <div className="container grid gap-8 md:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="text-3xl font-black">Conversemos sobre tu requerimiento técnico</h2>
          <p className="mt-4 leading-8 text-slate-300">Comparte tu aplicación, variable, carga o equipo de interés. Responderemos con una ruta clara de evaluación y cotización.</p>
          <div className="mt-6 flex flex-wrap gap-3"><a className="btn btn-primary" href={whatsappHref()}><Phone size={18} /> WhatsApp</a><a className="btn btn-secondary" href={`mailto:${siteConfig.email}`}><Mail size={18} /> Correo</a></div>
        </div>
        <div><h3 className="text-xl font-extrabold">Boletín técnico</h3><p className="mt-2 text-slate-300">Recibe guías y novedades técnicas cuando el canal esté habilitado.</p><div className="mt-5"><NewsletterForm /></div></div>
      </div>
    </section>
  );
}

function ContactPanel() {
  return <aside className="card h-fit p-6"><h2 className="text-xl font-extrabold text-navy">Canales directos</h2><ul className="mt-5 grid gap-4 text-slate-700"><li className="flex gap-3"><Mail className="text-cyan" /><a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a></li><li className="flex gap-3"><Phone className="text-cyan" /><a href={`tel:${siteConfig.telephone.replace(/\s/g, "")}`}>{siteConfig.telephone}</a></li><li className="flex gap-3"><MapPin className="text-cyan" /><span>{siteConfig.address}</span></li></ul><a className="btn btn-primary mt-6" href={whatsappHref()}>Escribir por WhatsApp</a></aside>;
}

function LegalPage({ title, items }: { title: string; items: string[] }) {
  return <><PageHero kicker="Legal" title={title} text="Información referencial para el uso del sitio y sus canales de contacto." /><section className="section"><div className="container max-w-3xl"><ul className="grid gap-4 text-lg leading-8 text-slate-700">{items.map((item) => <li key={item}>{item}</li>)}</ul></div></section></>;
}

function Spec({ title, items }: { title: string; items: string[] }) {
  return <div className="card p-6"><h2 className="text-xl font-extrabold text-navy">{title}</h2><ul className="mt-4 grid gap-3 text-slate-600">{items.map((item) => <li key={item}>{item}</li>)}</ul></div>;
}

function findBySlug<T extends { slug: string }>(items: readonly T[], slug?: string) {
  return items.find((item) => item.slug === slug);
}

function crumbsFor(sectionName: string, sectionHref: string, name: string, href: string) {
  return [{ name: "Inicio", href: "/" }, { name: sectionName, href: sectionHref }, { name, href }];
}
